import {getItemById} from '@shared/utilities';
import {
  BookingWorkspaceFormFields,
  ExcludeDates,
  Range,
  WorkspacesBookingList,
} from './booking-workspace-form.component';
import {
  areIntervalsOverlapping,
  isWithinInterval,
  startOfDay,
  endOfDay,
  subDays,
  addDays,
  differenceInDays,
} from 'date-fns';
import {WorkspaceStatuses, WorkspaceBookType} from '@shared/core';
import {isCoworking} from '@innowise-group/utilities';

const MAX_COWORKING_BOOKING_DAYS = 5;
const DAYS_TO_ADD_FOR_MAX_COWORKING = MAX_COWORKING_BOOKING_DAYS - 1;
const INCLUSIVE_DAY_OFFSET = 1; // Used to include both start and end day in day count

export const checkBookingDatesCollisions = (
  seat: BookingWorkspaceFormFields.Seat,
  list: WorkspacesBookingList,
  selectedStartDate: Date,
  selectedEndDate: Date,
  employeeId: string,
): boolean => {
  const selectedRange = {start: selectedStartDate, end: selectedEndDate};

  if (list?.length && selectedEndDate) {
    const selectedWorkspaceInfo = getItemById(list, seat);

    return selectedWorkspaceInfo?.excludeDates.some(
      (bookingStatus) =>
        areIntervalsOverlapping(selectedRange, bookingStatus.range) && bookingStatus.employeeId !== employeeId,
    );
  } else {
    return false;
  }
};

export const normalizePeriod = (period: Range): {start: Date; end: Date} => ({
  start: startOfDay(period.start),
  end: endOfDay(period.end),
});

export const getDatesToExclude = (
  list: WorkspacesBookingList,
  seat: string,
  editRequestId?: string,
  startDay?: string,
  endDay?: string,
): Range[] => {
  const currentWorkspace = getItemById(list, seat);

  if (!currentWorkspace) {
    return [];
  }

  let datesToProcess: ExcludeDates;

  if (editRequestId && startDay && endDay) {
    const editStartTime = startOfDay(new Date(startDay)).getTime();
    const editEndTime = startOfDay(new Date(endDay)).getTime();

    datesToProcess = currentWorkspace.excludeDates.filter((status) => {
      const statusStartTime = startOfDay(status.range.start).getTime();
      const statusEndTime = startOfDay(status.range.end).getTime();

      return !(statusStartTime === editStartTime && statusEndTime === editEndTime);
    });
  } else if (editRequestId) {
    datesToProcess = currentWorkspace.excludeDates;
  } else {
    const {excludeDates = [], activeDates = []} = currentWorkspace;
    datesToProcess = [...excludeDates, ...activeDates];
  }

  return datesToProcess
    .map((status) => normalizePeriod({start: status.range.start, end: status.range.end}))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
};

export function findFirstAvailableDate(periods: Range[]): Date {
  let currentDate = startOfDay(new Date());

  if (periods.length) {
    for (const period of periods) {
      if (currentDate >= period.start && currentDate <= period.end) {
        currentDate = addDays(startOfDay(new Date(period.end)), 1);
      }
    }
  }
  return currentDate;
}

export const checkIsTemporaryBookedSeat = (workspacesList: WorkspacesBookingList, seatId: string) => {
  return (
    workspacesList &&
    workspacesList.some(
      (ws) =>
        ws.id === seatId && (ws.status === WorkspaceStatuses.CoworkingBooked || ws.status === WorkspaceStatuses.Booked),
    )
  );
};

/**
 * Checks if a date falls within any excluded period
 * Note: excludedPeriods should be normalized and sorted (as returned by getDatesToExclude)
 */
export const isDateInExcludedPeriods = (date: Date, excludedPeriods: Range[]): boolean => {
  const normalizedDate = startOfDay(date);

  return excludedPeriods.some((period) => {
    return isWithinInterval(normalizedDate, period);
  });
};

/**
 * Finds the next available date after a given date that doesn't overlap with excluded periods
 * Note: excludedPeriods should be normalized and sorted (as returned by getDatesToExclude)
 */
export const findNextAvailableDateAfter = (date: Date, excludedPeriods: Range[]): Date => {
  let currentDate = startOfDay(date);

  if (!excludedPeriods.length) {
    return currentDate;
  }

  for (const period of excludedPeriods) {
    if (isWithinInterval(currentDate, period)) {
      currentDate = addDays(startOfDay(new Date(period.end)), 1);
    }
  }

  return currentDate;
};

/**
 * Finds a valid endDay given a startDay and original endDay that doesn't overlap with excluded periods
 * Returns the latest possible endDay that doesn't cause overlap
 * Note: excludedPeriods should be normalized and sorted (as returned by getDatesToExclude)
 */
export const findValidEndDay = (startDay: Date, originalEndDay: Date, excludedPeriods: Range[]): Date => {
  if (!excludedPeriods.length) {
    return originalEndDay >= startDay ? originalEndDay : startDay;
  }

  let validEndDay = originalEndDay;

  for (const period of excludedPeriods) {
    if (isWithinInterval(originalEndDay, period) && startDay < period.start) {
      const candidateEndDay = subDays(startOfDay(new Date(period.start)), 1);
      return candidateEndDay >= startDay ? candidateEndDay : startDay;
    }

    const currentRange = {start: startDay, end: validEndDay};
    if (areIntervalsOverlapping(currentRange, period) && startDay < period.start) {
      validEndDay = subDays(startOfDay(new Date(period.start)), 1);
      if (validEndDay < startDay) {
        validEndDay = startDay;
      }
      break;
    }
  }

  return validEndDay >= startDay ? validEndDay : startDay;
};

/**
 * Validates and adjusts booking dates based on excluded periods and coworking limits
 * @param startDay - Initial start day as string
 * @param endDay - Initial end day as string
 * @param watchSeat - Selected seat ID
 * @param excludedDatesForValidation - Array of excluded date ranges
 * @param seatStatus - Status of the selected workspace
 * @returns Object with validated startDay and endDay as Date objects, or undefined if validation cannot proceed
 */
export const validateBookingDates = (
  startDay: string | undefined,
  endDay: string | undefined,
  watchSeat: string | undefined,
  excludedDatesForValidation: Range[],
  seatStatus: WorkspaceBookType | undefined,
): {startDay: Date | undefined; endDay: Date | undefined} => {
  if (!startDay || !endDay || !watchSeat) {
    return {startDay: startDay ? new Date(startDay) : undefined, endDay: endDay ? new Date(endDay) : undefined};
  }

  let adjustedStartDay = startOfDay(new Date(startDay));
  let adjustedEndDay = startOfDay(new Date(endDay));

  if (excludedDatesForValidation.length > 0) {
    if (isDateInExcludedPeriods(adjustedStartDay, excludedDatesForValidation)) {
      adjustedStartDay = findNextAvailableDateAfter(adjustedStartDay, excludedDatesForValidation);
      if (adjustedStartDay > adjustedEndDay) {
        adjustedEndDay = adjustedStartDay;
      }
    }

    adjustedEndDay = findValidEndDay(adjustedStartDay, adjustedEndDay, excludedDatesForValidation);
  }

  if (isCoworking(seatStatus)) {
    const numberOfDays = differenceInDays(adjustedEndDay, adjustedStartDay) + INCLUSIVE_DAY_OFFSET;
    if (numberOfDays > MAX_COWORKING_BOOKING_DAYS) {
      adjustedEndDay = addDays(adjustedStartDay, DAYS_TO_ADD_FOR_MAX_COWORKING);
    }
  }

  return {
    startDay: adjustedStartDay,
    endDay: adjustedEndDay,
  };
};
